import { Button, Input, List, Popover, Space, Tag, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { Fragment, useEffect, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { LuArchiveRestore } from 'react-icons/lu';
import { useParams } from 'react-router-dom';
import { PriorityOptions, TASk_TABS } from '../../constants';
import { isAdmin, isManager, isSuperAdmin, priorityWithIcon } from '../../helper';
import useLabels from '../../hooks/useLabels';
import useRestaurants from '../../hooks/useRestaurants';
import useTasks from '../../hooks/useTasks';
import { getTasks, updateTaskProgress } from '../../redux/actions/tasks';
import { getHighlightedText } from '../../utils';
import ChecklistProgress from '../ChecklistProgress';
import DraggableTable from '../DraggableTable';
import DueDateBadge from '../DueDateBadge';
import DueDateFilter from '../DueDateFilter';
import TaskDetailView from '../TaskDetailView';
import UserAvatar from '../UserAvatar';
import UserGroup from '../UserGroup';

const ArchivedTask = ({ restaurant }) => {
  const { id } = useParams();
  const {
    archived: { tasks, pagination },
  } = useTasks();
  const { users } = useRestaurants();
  const { labels: allLabels } = useLabels();

  const [filteredInfo, setFilteredInfo] = useState({});
  const [isTaskView, setIsTaskView] = useState(false);
  const [taskData, setTaskData] = useState();
  const [currentPage, setCurrentPage] = useState(1);

  const handleTableChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setCurrentPage(pagination.current);

    const { due_date, ...restFilters } = filters;
    const dateRange = due_date?.[0];
    const start_date = dateRange?.[0] ? dayjs(dateRange[0], "DD/MM/YYYY").format("YYYY-MM-DD") : undefined;
    const end_date = dateRange?.[1] ? dayjs(dateRange[1], "DD/MM/YYYY").format("YYYY-MM-DD") : undefined;

    getTasks({
      id,
      filter: TASk_TABS.ARCHIVED,
      page: pagination.current,
      ...restFilters,
      start_date,
      end_date,
    });
  };

  const handleUnArchive = async (record) => {
    const res = await updateTaskProgress(record?.id, { progress: record.completed_by ? 1 : 0 }, { toast: false });
    if (res.ack)
      getTasks({
        id,
        filter: TASk_TABS.ARCHIVED,
        page: currentPage,
      });
  };

  const columns = [
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      hidden: !(isSuperAdmin() || isAdmin() || isManager()),
      render: (_, record) => {
        return (
          <Tooltip title="Unarchive Task">
            <Button
              icon={<LuArchiveRestore style={{ display: 'flex' }} />}
              ghost
              variant="dashed"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleUnArchive(record);
              }}
              size="small"
            />
          </Tooltip>
        );
      },
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      filteredValue: filteredInfo.title || null,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div className="task-header-search">
          <Input
            placeholder="Search title..."
            size="large"
            allowClear
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys([e.target.value?.trimStart()])}
            onPressEnter={() => confirm()}
            suffix={<FiSearch />}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} icon={<FiSearch />} size="small">
              Search
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                confirm();
              }}
              color="danger"
              variant="filled"
              size="small"
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <FiSearch style={{ color: filtered ? '#1677ff' : undefined }} />,
      render: (title, record) => {
        const searchText = filteredInfo?.title?.[0]?.toLowerCase();
        return (
          <Fragment>
            <Typography.Text strong ellipsis={{ tooltip: true }}>
              {getHighlightedText(title, searchText)}
            </Typography.Text>
          </Fragment>
        );
      },
    },
    {
      title: 'Assignee',
      dataIndex: 'assignees',
      key: 'assignees',
      width: 120,
      filteredValue: filteredInfo.assignees || null,
      filters: users?.map((user) => {
        return {
          text: (
            <Space align="center">
              <UserAvatar user={user} size="small" style={{ marginRight: 8 }} />
              {`${user.first_name} ${user.last_name ?? ''}`}
            </Space>
          ),
          value: user.id,
        };
      }),
      render: (assignees) => {
        return (
          <UserGroup users={assignees} maxCount={2} />
        );
      },
    },
    {
      title: 'Checklist',
      dataIndex: 'checklist',
      key: 'checklist',
      width: 120,
      render: (checklist) => {
        return <ChecklistProgress trackCheckList={checklist} />;
      },
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      filteredValue: filteredInfo.priority || null,
      filters: PriorityOptions.map((option) => ({
        text: option.label,
        value: option.value,
      })),
      render: (priority) => {
        return priorityWithIcon(priority);
      },
    },
    {
      title: 'Due Date',
      dataIndex: 'due_date',
      key: 'due_date',
      width: 220,
      filteredValue: filteredInfo.due_date || null,
      filterDropdown: (props) => <DueDateFilter {...props} />,
      render: (date, record) => {
        return <DueDateBadge date={date} completedAt={record.completed_at} />;
      },
    },
    {
      title: 'Labels',
      dataIndex: 'labels',
      key: 'labels',
      width: 150,
      filteredValue: filteredInfo.labels || null,
      filters: allLabels?.map((label) => ({
        text: <Tag color={label.color}>{label.title}</Tag>,
        value: label.id,
      })),
      render: (labels) => {
        const visibleLabel = labels && labels[0];
        const otherLabels = labels?.slice(1);

        return (
          <>
            {visibleLabel && <Tag color={visibleLabel?.color}>{visibleLabel?.title}</Tag>}
            {!!otherLabels?.length && (
              <Popover
                title="Other Labels"
                trigger="hover"
                style={{ padding: 0 }}
                content={
                  <List
                    size="small"
                    dataSource={otherLabels}
                    renderItem={(label) => (
                      <List.Item style={{ padding: '4px 0' }} key={label?.label_id}>
                        <Tag style={{ width: '100%' }} color={label?.color}>
                          {label?.title}
                        </Tag>
                      </List.Item>
                    )}
                  />
                }
              >
                <Tag color="blue">+{otherLabels.length}</Tag>
              </Popover>
            )}
          </>
        );
      },
    },
    {
      title: 'Info',
      dataIndex: 'archived_at',
      key: 'archived_at',
      width: 150,
      hidden: isSuperAdmin() || isAdmin() || isManager(),
      render: (archivedAt) => {
        if (!archivedAt) return '-';
        const expiryDate = dayjs(archivedAt).add(30, 'day');
        const daysLeft = expiryDate.diff(dayjs(), 'day');

        let color = 'inherit';
        if (daysLeft <= 5) color = '#ff4d4f';
        else if (daysLeft <= 15) color = '#faad14';

        return (
          <Typography.Text type="secondary" style={{ fontSize: 13, color }}>
            {daysLeft <= 0 ? 'Disappearing soon' : `Disappearing in ${daysLeft} days`}
          </Typography.Text>
        );
      },
    },
  ];

  useEffect(() => {
    getTasks({ id, filter: TASk_TABS.ARCHIVED, page: currentPage });
  }, []);

  return (
    <div className="ArchivedTask">
      <DraggableTable
        columns={columns}
        onRow={(record) => ({
          onClick: () => {
            setTaskData(record);
            setIsTaskView(true);
          },
        })}
        data={tasks?.map((task) => ({ ...task, key: task.id }))}
        onOrderChange={() => { }}
        onChange={handleTableChange}
        pagination={{
          total: pagination?.total,
          pageSize: pagination?.limit,
          current: pagination?.page,
          showSizeChanger: false,
          showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} Records`,
        }}
      />

      {isTaskView && (
        <TaskDetailView isView open={isTaskView} task={taskData} restaurant={restaurant} onClose={() => setIsTaskView(false)} />
      )}
    </div>
  );
};

export default ArchivedTask;
